/**
 * 메시지 조작 오케스트레이션 — 결정 #16. core의 순수 함수(message-ops)를
 * 저장소에 연결한다. 모든 쓰기는 storage 포트를 거쳐 sync 메타가 갱신된다(#20).
 */
import {
  buildFork,
  deleteVariant as deleteVariantOp,
  editActiveVariant,
  selectVariant,
  swipe as swipeOp,
  type Message,
} from "@roqeet/core";
import { storage } from "./storage.js";

/** liveMessages를 1회 읽어 현재 메시지 배열을 얻는다(컨텍스트/분기용). */
async function readMessages(chatId: string): Promise<Message[]> {
  const live = storage.liveMessages(chatId);
  return new Promise<Message[]>((resolve) => {
    const unsub = live.subscribe((v) => {
      resolve(v);
      queueMicrotask(unsub);
    });
  });
}

/** 활성 variant 텍스트를 편집해 저장(결정 #16). 기본으로 편집 이력을 보존. */
export async function editMessage(
  messageId: string,
  newContent: string,
  keepHistory = true,
): Promise<void> {
  const m = await storage.getMessage(messageId);
  if (!m) return;
  await storage.putMessage(editActiveVariant(m, newContent, { keepHistory }));
}

/** 스와이프(이전/다음 variant). */
export async function swipeMessage(messageId: string, dir: -1 | 1): Promise<void> {
  const m = await storage.getMessage(messageId);
  if (!m) return;
  const next = swipeOp(m, dir);
  if (next !== m) await storage.putMessage(next);
}

/** 특정 variant 선택. */
export async function selectMessageVariant(messageId: string, index: number): Promise<void> {
  const m = await storage.getMessage(messageId);
  if (!m) return;
  const next = selectVariant(m, index);
  if (next !== m) await storage.putMessage(next);
}

/** 현재 활성 variant 삭제(마지막 하나는 유지, 결정 #16). */
export async function deleteActiveVariant(messageId: string): Promise<void> {
  const m = await storage.getMessage(messageId);
  if (!m) return;
  const next = deleteVariantOp(m, m.activeVariant);
  if (next !== m) await storage.putMessage(next);
}

/** 메시지 전체를 소프트 삭제(tombstone, 결정 #20). */
export async function deleteMessage(messageId: string): Promise<void> {
  await storage.deleteMessage(messageId);
}

/**
 * 이 메시지 시점까지 대화를 분기한다(결정 #16: 경량 세션 포크). 새 Chat과 복제
 * 메시지를 저장하고 새 chatId를 반환한다.
 */
export async function forkChatAt(chatId: string, atOrder: number): Promise<string | undefined> {
  const chat = await storage.chats.get(chatId);
  if (!chat) return undefined;
  const messages = await readMessages(chatId);
  const { chat: forkChat, messages: forkMessages } = buildFork(chat, messages, atOrder);

  await storage.chats.put(forkChat);
  await storage.putMessages(forkMessages);
  return forkChat.id;
}
